import { filter } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import {
  Link,
  ErrorHandler,
  ExternalLink,
  Gutter,
  StatusBadge,
  Text,
  TextFilterSearch,
} from '@common/components';

import { RolesQuery, RolesResponse } from '@clinician-profile/graphql';
import {
  MissionControlPaginatedStaffingRequestsQueryVariables,
  MissionControlStaffingRequestFilterInput,
  OrganizationBusinessesQueryCgResponse,
  WorksClinicalUnitsQueryCgResponse,
} from '@common/graphql';
import { useLazyQuery, usePaginationHelper, useQuery } from '@common/hooks';
import {
  ORGANIZATION_BUSINESSES_QUERY,
  WORKS_CLINICAL_UNITS_QUERY,
} from '@institutions/graphql';
import { JobActionsDropdown } from '@jobs/components';
import {
  MISSION_CONTROL_PAGINATED_STAFFING_REQUESTS_QUERY,
  MissionControlStaffingRequestType,
} from '@jobs/graphql';
import { nonFalse } from '@pay-bill/services';
import {
  buildOptionsFromData,
  FilteredTable,
  MultiSelectTableFilter,
} from 'works/components/filtered-table';
import { RenderTableColumn } from 'works/components/filtered-table/render-filtered-table';
import { TextSearchTableFilter } from 'works/components/filtered-table/text-search-table-filter';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';

export type MissionControlStaffingTableKindType =
  | 'request_at_risk'
  | 'reviews_pending';

type Props = {
  tableKind: MissionControlStaffingTableKindType;
};

export const MissionControlStaffingTableJobsBase: React.FC<Props> = ({
  tableKind,
}) => {
  const isRequestAtRisk = tableKind === 'request_at_risk';
  const isReviewsPending = tableKind === 'reviews_pending';

  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: `MissionControlStaffingJobsTable-${tableKind}`,
    defaultPaginationData: {
      defaultPageSize: 10,
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'SECONDS_UNTIL_START_DATE_AND_APPLICATIONS_ORGANIZATION',
    },
    columnsDictionary: {
      code: 'JOB_ID',
      role: 'ROLE',
      startDate: 'START',
      secondsUntilStartDate:
        'SECONDS_UNTIL_START_DATE_AND_APPLICATIONS_ORGANIZATION',
      organizationBusiness: 'ORGANIZATION_BUSINESS',
      clinicalUnit: 'CLINICAL_UNIT',
      openStaffingRequestPositionsCount: 'OPEN',
      staffingRequestApplicationsCount: 'APPLICATIONS_ORGANIZATION',
      offeredOffersStaffingRequestApplicationsCount: 'OFFER',
      inReviewStaffingRequestApplicationsCount: 'IN_REVIEW',
      hiringLeader: 'HIRING_LEADER',
    },
  });

  const [fetchOrgBusiness, { data: orgBusinessData, error: orgBusinessError }] =
    useLazyQuery(ORGANIZATION_BUSINESSES_QUERY);

  const [fetchRoles, { data: rolesData, error: rolesError }] =
    useLazyQuery(RolesQuery);

  const [
    fetchClinicalUnits,
    { data: clinicalUnitData, error: clinicalUnitError },
  ] = useLazyQuery(WORKS_CLINICAL_UNITS_QUERY);

  const { data, loading, error } = useQuery(
    MISSION_CONTROL_PAGINATED_STAFFING_REQUESTS_QUERY,
    {
      variables: {
        limit: paginationVariables.limit,
        offset: paginationVariables.offset,
        orderDirection:
          paginationVariables.orderDirection as MissionControlPaginatedStaffingRequestsQueryVariables['orderDirection'],
        orderField:
          paginationVariables.orderField as MissionControlPaginatedStaffingRequestsQueryVariables['orderField'],
        filters: {
          ...paginationVariables.filters,
          state: isRequestAtRisk ? ['OPEN'] : undefined,
          applicationState: isReviewsPending ? ['in_review'] : undefined,
        },
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      onCompleted: () => {
        fetchOrgBusiness();
        fetchRoles();
        fetchClinicalUnits();
      },
    }
  );

  const dataSource = useMemo(
    () => data?.missionControlPaginatedStaffingRequests?.staffingRequests || [],
    [data]
  );

  const totalCount =
    data?.missionControlPaginatedStaffingRequests?.totalCount || 0;

  const columns = useMemo(() => {
    return getColumns({
      filters: filters as MissionControlStaffingRequestFilterInput,
      orgBusinessData,
      clinicalUnitData,
      rolesData,
      isRequestAtRisk,
      isReviewsPending,
    }) as unknown as RenderTableColumn<MissionControlStaffingRequestType>[];
  }, [
    filters,
    orgBusinessData,
    clinicalUnitData,
    rolesData,
    isRequestAtRisk,
    isReviewsPending,
  ]);

  const anyError = error || orgBusinessError || clinicalUnitError || rolesError;
  const filtersDataReady =
    !!orgBusinessData && !!clinicalUnitData && !!rolesData;

  if (anyError) {
    return <ErrorHandler error={anyError} log="MissionControlStaffingTable" />;
  }

  return (
    <>
      <FilteredTable
        rowKey="id"
        className="mission-control-staffing-table"
        columns={columns}
        dataSource={dataSource}
        pagination={{ ...paginationVariables, total: totalCount }}
        loading={loading}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        filters={filters}
        localStorageFilterKey={localStorageFilterKey}
        filtersDataReady={filtersDataReady}
      />
      {!loading && (
        <Gutter size="sm" direction="vertical">
          Showing {dataSource.length}.{' '}
          <ExternalLink url="/works/staffing-requests">View All</ExternalLink>.
        </Gutter>
      )}
    </>
  );
};

const getColumns = ({
  filters,
  orgBusinessData,
  clinicalUnitData,
  rolesData,
  isRequestAtRisk,
  isReviewsPending,
}: {
  filters: MissionControlStaffingRequestFilterInput;
  orgBusinessData?: OrganizationBusinessesQueryCgResponse;
  clinicalUnitData?: WorksClinicalUnitsQueryCgResponse;
  rolesData?: RolesResponse;
  isRequestAtRisk: boolean;
  isReviewsPending: boolean;
}) => {
  return filter<
    RenderTableColumn<MissionControlStaffingRequestType> | false,
    RenderTableColumn<MissionControlStaffingRequestType>
  >(
    [
      {
        title: 'Job ID',
        dataIndex: 'code',
        key: 'code',
        width: 105,
        filtered: !!filters.code,
        filteredValue: filters.code || null,
        filterDropdown: TextSearchTableFilter,
        sorter: true,
        render: (_: unknown, record: MissionControlStaffingRequestType) => (
          <Link to={`/works/staffing-requests/${record.id}`} target="_blank">
            <Text bold color="elm">
              {record.code}
            </Text>
          </Link>
        ),
      },
      {
        title: 'Role',
        dataIndex: ['role', 'abbreviation'],
        key: 'role',
        width: 140,
        sorter: true,
        filtered: !!filters.role,
        filteredValue: filters.role || null,
        filters:
          rolesData?.roles &&
          buildOptionsFromData(
            rolesData.roles,
            (role: { id: string; abbreviation: string }) => ({
              text: role.abbreviation,
              value: role.id,
            })
          ),
        filterDropdown: MultiSelectTableFilter,
      },
      {
        title: 'Start Date',
        dataIndex: 'startDate',
        key: 'startDate',
        width: 87,
        sorter: true,
        render: (startDate: string) => toUSCompactDateFormat(startDate),
      },
      {
        title: 'Lead Time',
        dataIndex: 'secondsUntilStartDate',
        key: 'secondsUntilStartDate',
        width: 130,
        sorter: true,
        render: (secondsUntilStartDate, record) => {
          const leadTimeType = isRequestAtRisk
            ? record.timeUntilStartDateForUpcomingJobsType
            : record.timeUntilStartDateForReviewsPendingType;

          return (
            <StatusBadge
              value={moment
                .duration(secondsUntilStartDate, 'seconds')
                .humanize(true)}
              type={leadTimeType}
            />
          );
        },
      },
      {
        title: 'Facility',
        dataIndex: ['organizationBusiness', 'name'],
        key: 'organizationBusiness',
        width: 264,
        sorter: true,
        filtered: !!filters.organizationBusiness,
        filteredValue: filters.organizationBusiness || null,
        filters: buildOptionsFromData(
          orgBusinessData?.organizationBusinesses || [],
          (organizationBusiness: { id: string; name: string }) => ({
            value: organizationBusiness.id,
            text: organizationBusiness.name,
          })
        ),
        filterDropdown: MultiSelectTableFilter,
      },
      {
        title: 'Unit',
        dataIndex: ['organizationUnit', 'clinicalUnit', 'displayName'],
        key: 'clinicalUnit',
        width: 264,
        sorter: true,
        filtered: !!filters.clinicalUnit,
        filteredValue: filters.clinicalUnit || null,
        filters:
          clinicalUnitData &&
          buildOptionsFromData(
            clinicalUnitData.worksClinicalUnits,
            (worksClinicalUnit: { id: string; displayName: string }) => ({
              value: worksClinicalUnit.id,
              text: worksClinicalUnit.displayName,
            })
          ),
        filterDropdown: MultiSelectTableFilter,
      },
      isReviewsPending && {
        title: 'Hiring Manager',
        dataIndex: ['hiringLeader', 'name'],
        key: 'hiringLeader',
        width: 130,
        sorter: true,
        filtered: !!filters.hiringLeader,
        filteredValue: filters.hiringLeader || null,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <Gutter internal size="md">
            <TextFilterSearch
              onSearch={(value) => {
                value ? setSelectedKeys([value]) : setSelectedKeys([]);
                confirm();
              }}
            />
          </Gutter>
        ),
      },
      {
        title: 'Open Positions',
        dataIndex: 'openStaffingRequestPositionsCount',
        key: 'openStaffingRequestPositionsCount',
        width: 100,
        sorter: true,
      },
      isRequestAtRisk && {
        title: 'Applications',
        dataIndex: 'staffingRequestApplicationsCount',
        key: 'staffingRequestApplicationsCount',
        width: 83,
        sorter: true,
      },
      isRequestAtRisk && {
        title: 'Offer',
        dataIndex: 'offeredOffersStaffingRequestApplicationsCount',
        key: 'offeredOffersStaffingRequestApplicationsCount',
        width: 83,
        sorter: true,
      },
      isReviewsPending && {
        title: 'In Review',
        dataIndex: 'inReviewStaffingRequestApplicationsCount',
        key: 'inReviewStaffingRequestApplicationsCount',
        width: 80,
        sorter: true,
      },
      {
        title: 'Action',
        render: (_: unknown, job: MissionControlStaffingRequestType) => (
          <JobActionsDropdown
            job={job}
            refetchQueries={['MissionControlPaginatedStaffingRequests']}
          />
        ),
      },
    ],
    nonFalse
  );
};
